import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, CalendarCheck, ChevronLeft, ChevronRight, Plus } from "lucide-react";

import { api } from "@/lib/api";
import { AppointmentItem, dayLabel, ymd } from "@/components/appointment-item";
import { Header } from "@/components/header";
import { ChipRow, Empty, ErrorView, IconButton, Loading } from "@/components/kit";

const WEEK = ["L", "M", "M", "J", "V", "S", "D"];

function MonthGrid({ month, days, sel, today, byDay, onPick, shift }) {
  return (
    <div className="rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-3">
      <div className="mb-2 flex items-center">
        <button type="button" aria-label="Mois précédent" data-testid="planning-prev-month" onClick={() => shift(-1)} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/5"><ChevronLeft className="h-5 w-5" /></button>
        <h2 data-testid="planning-month-label" className="t-h3 flex-1 text-center capitalize">{month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h2>
        <button type="button" aria-label="Mois suivant" data-testid="planning-next-month" onClick={() => shift(1)} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/5"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className="grid grid-cols-7">{WEEK.map((w, i) => <span key={i} className="t-caption text-center">{w}</span>)}</div>
      <div className="mt-1 grid grid-cols-7">
        {days.map((d) => {
          const k = ymd(d);
          const inMonth = d.getMonth() === month.getMonth();
          const isSel = k === sel, isToday = k === today;
          const n = byDay[k]?.length ?? 0;
          return (
            <button key={k} type="button" data-testid={`planning-day-${k}`} aria-pressed={isSel} aria-label={`${dayLabel(k)}${n ? ` · ${n} rendez-vous` : ""}`} onClick={() => onPick(d, inMonth)}
              className="focus-ring flex h-12 flex-col items-center justify-center rounded-xl hover:bg-white/5 md:h-14">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm ${isSel ? "bg-[#D4AF37] font-bold text-[#121212]" : inMonth ? "text-white" : "text-[#555555]"} ${isToday && !isSel ? "border border-[#D4AF37]" : ""}`}>{d.getDate()}</span>
              <span className="mt-px flex h-[5px] gap-0.5">{Array.from({ length: Math.min(n, 3) }).map((_, i) => <span key={i} className="h-1 w-1 rounded-full bg-[#D4AF37]" />)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Planning() {
  const navigate = useNavigate();
  const today = ymd(new Date());
  // Selected day + filter live in the URL so "back" from the appointment form restores the same view.
  const [params, setParams] = useSearchParams();
  const sel = /^\d{4}-\d{2}-\d{2}$/.test(params.get("d") || "") ? params.get("d") : today;
  const filter = params.get("f") === "mine" ? "mine" : "all";
  const setQ = (k, v) => setParams((p) => { const n = new URLSearchParams(p); n.set(k, v); return n; }, { replace: true });
  const setSel = (v) => setQ("d", v);
  const setFilter = (v) => setQ("f", v);
  const [month, setMonth] = useState(() => { const d = new Date(`${sel}T12:00`); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const days = useMemo(() => {
    const first = new Date(month);
    first.setDate(1 - ((month.getDay() + 6) % 7)); // lundi
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(first); d.setDate(first.getDate() + i); return d; });
  }, [month]);
  const rangeStart = `${ymd(days[0])}T00:00`;
  const rangeEnd = `${ymd(new Date(days[41].getFullYear(), days[41].getMonth(), days[41].getDate() + 1))}T00:00`;

  const q = useQuery({
    queryKey: ["admin-appointments", rangeStart, filter],
    queryFn: () => api(`/admin/appointments?start=${rangeStart}&end=${rangeEnd}${filter === "mine" ? "&mine=true" : ""}`),
  });
  const nowKey = `${today}T00:00`;
  const upcoming = useQuery({
    queryKey: ["admin-appointments", "upcoming", nowKey],
    queryFn: () => api(`/admin/appointments?start=${nowKey}&mine=true`),
    enabled: filter === "mine",
  });
  const byDay = useMemo(() => {
    const m = {};
    (q.data ?? []).forEach((a) => {
      const cur = new Date(`${a.start.slice(0, 10)}T12:00`);
      const last = a.end.slice(0, 10);
      for (let i = 0; i < 62 && ymd(cur) <= last; i++) {
        (m[ymd(cur)] ??= []).push(a);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return m;
  }, [q.data]);
  const list = byDay[sel] ?? [];
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const onPick = (d, inMonth) => { setSel(ymd(d)); if (!inMonth) setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); };

  return (
    <div data-testid="planning-screen" className="flex flex-col">
      <Header title="Planning" subtitle="Gérant & collaborateurs"
        right={<IconButton icon={CalendarCheck} label="Aujourd'hui" testID="planning-today-button" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setSel(today); }} />} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#222222] px-4 md:px-8">
        <ChipRow options={[{ key: "all", label: "Équipe · tous les RDV" }, { key: "mine", label: "Mon espace" }]} value={filter} onChange={setFilter} testIDPrefix="planning-filter" />
        <button type="button" data-testid="planning-add-button" onClick={() => navigate(`/admin/appointment-form?date=${sel}`)}
          className="focus-ring fixed bottom-20 right-4 z-20 flex h-14 items-center gap-2 rounded-full bg-[#D4AF37] px-5 font-bold text-[#121212] shadow-xl transition-colors hover:bg-[#E2C25A] lg:static lg:h-11 lg:rounded-xl lg:shadow-none">
          <Plus className="h-[22px] w-[22px]" /> Rendez-vous
        </button>
      </div>
      <div className="mx-auto grid w-full max-w-6xl items-start gap-6 p-4 pb-28 md:p-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:pb-8">
        <div className="lg:sticky lg:top-6"><MonthGrid month={month} days={days} sel={sel} today={today} byDay={byDay} onPick={onPick} shift={shift} /></div>
        <div className="flex flex-col gap-4">
          <h2 className="t-h3 first-letter:uppercase" data-testid="planning-selected-day">{dayLabel(sel)}</h2>
          {q.isLoading ? <Loading /> : q.isError ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : list.length ? (
            <div className="flex flex-col gap-3">{list.map((a) => <AppointmentItem key={a.id} a={a} />)}</div>
          ) : (
            <Empty icon={CalendarDays} title="Aucun rendez-vous" text={filter === "mine" ? "Aucun rendez-vous pour vous ce jour-là." : "Ajoutez un rendez-vous pour ce jour avec le bouton « Rendez-vous »."} />
          )}
          {filter === "mine" ? (
            <section data-testid="planning-my-upcoming" className="flex flex-col gap-3">
              <h2 className="t-h3">Mes prochains rendez-vous</h2>
              {upcoming.isLoading ? <Loading /> : (upcoming.data ?? []).length ? (upcoming.data ?? []).slice(0, 20).map((a) => <AppointmentItem key={a.id} a={a} showDate />)
                : <p className="t-caption">Aucun rendez-vous à venir dans votre espace.</p>}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
